import { filterByDocumentType } from 'Utility/documentHandling';
import { defineDocConfig } from './utils';

import type { ClinicalArchive } from '.';

export const sleepApneaDocumentTypes = defineDocConfig([
  {
    name: 'diagnosis',
    subTypes: ['diagnosis', 'sleepApneaFirstVisit', 'respiratoryFirstVisit', 'sleepApneaSeverity', 'performanceRating'],
  },
  {
    name: 'clpAndCnpTests',
    subTypes: ['sleepPolygraphy', 'sleepStudy', 'spirometry', 'nightOximetry', 'carbonDioxideMeasurement'],
    secondaryDocs: ['madTherapy', 'diagnosis'],
  },
  { name: 'examination' },
  { name: 'medication', subTypes: ['medication', 'adverseEffect'] },
  {
    name: 'treatment',
    subTypes: [
      'papTherapy',
      'hfncTherapy',
      'oxygenTherapy',
      'madTherapy',
      'sleepApneaSurgicalTreatment',
      'otherTreatment',
      'patientDoesNotWantRespiratorySupportTherapy',
    ],
    secondaryDocs: ['diagnosis', 'sleepApneaFirstVisit', 'respiratoryFirstVisit', 'sleepPolygraphy', 'sleepStudy'],
  },
  { name: 'checklist' },
  {
    name: 'comorbidity',
    subTypes: ['comorbidity', 'sickLeave'],
    secondaryDocs: ['diagnosis'],
    title: 'otherHealth',
  },
  { name: 'background', subTypes: ['background', 'measurement'] },
  { name: 'myService', title: 'titleShort' },
]);

export const sleepApneaGraphDocTypes: TDocumentType[] = [
  'diagnosis',
  'medication',
  'adverseEffect',
  'papTherapy',
  'hfncTherapy',
  'oxygenTherapy',
  'madTherapy',
  'sleepApneaSurgicalTreatment',
  'otherTreatment',
  'patientDoesNotWantRespiratorySupportTherapy',
  'sleepPolygraphy',
  'sleepStudy',
  'spirometry',
  'nightOximetry',
  'carbonDioxideMeasurement',
  'comorbidity',
  'background',
  'measurement',
] satisfies TDocumentType[];

const respiratoryFailureDGs = new Set<TDiagnosis>(['J96.1', 'J96.9', 'respiratoryFailureSuspicion']);

const deviceTypeOptions = new Set<IPapDevice['type']>([
  'constantPressure2PV',
  'selfAdjusting2PV',
  'adaptiveServoVentilator',
  'automaticAdaptiveServoVentilator',
  'VAPS',
]);

// Filter these routes/types from showing in page navigations based on these rules
export const sleepApneaFilteredTypes = (docs: IDocumentV2[]): string[] => {
  let diag: IDocumentWithTypeV2<'diagnosis'> | undefined = undefined;

  if (docs.length > 0) {
    diag = filterByDocumentType(docs, 'diagnosis').find(d => d.diagnosis && respiratoryFailureDGs.has(d.diagnosis));
  }
  return diag ? [] : ['checklist']; // Filter checklist if no Respiratory dx
};

export const sleepApneaClinicalArchiveTypes: readonly ClinicalArchive[] = ['KEU', 'HOI'] as const;

export const sleepApneaAlerts: IAlert[] = [
  {
    // Notification to the user if respiratory failure treatments are recorded but no respiratory failure diagnosis is found
    content: 'alerts.sleepApnea.noRespiratoryFailureDiagnosis',
    id: 'noRespiratoryFailureDiagnosis',
    severity: 'warning',
    popup: true,
    rule: (docs?: Array<IDocumentV2>): boolean => {
      const respiratoryDG = docs?.find(
        d => d._type === 'diagnosis' && d.diagnosis && respiratoryFailureDGs.has(d.diagnosis),
      );
      if (respiratoryDG) return false;

      const hasOxygenOrHFNCDoc = docs?.find(d => d._type === 'oxygenTherapy' || d._type === 'hfncTherapy');

      const hasPapTherapyWithRelevantDevice = docs?.find(d => {
        if (d._type === 'papTherapy') {
          // If papTherapy includes a device that is not a CPAP device
          return d.devices?.find(device => deviceTypeOptions.has(device.type));
        }
        return false;
      });
      return !!hasOxygenOrHFNCDoc || !!hasPapTherapyWithRelevantDevice;
    },
  },
];
